import { useState } from 'react';
import { Server } from 'lucide-react';
import { InfoPanel } from '@/components/shared/InfoPanel';
import { IconSectionHeader } from '@/components/shared/IconSectionHeader';
import { AgentServerList } from '@/components/agent/AgentServerList';
import { ServiceLogSheet, type ServiceLogTarget } from '@/components/agent/ServiceLogSheet';
import { useServiceStatuses } from '@/hooks/agents/useServiceStatuses';
import type { Agent } from '@/lib/types';

/**
 * Detail-panel section listing the servers the agent registered on its
 * sandbox (Storybook, dev servers, watchers) with ports, info lines, start
 * times, and tunnel links. Polls the live on-box service state while
 * mounted so each server's dot reflects whether its process is actually
 * running, and opens a per-server log sheet. Renders nothing when the
 * sandbox is gone or no servers are registered, so the panel stays quiet
 * for the common case.
 */
export function AgentDetailServersSection({ agent }: { agent: Agent }) {
  const daemons = agent.devboxId ? (agent.daemons ?? []) : [];
  const [logTarget, setLogTarget] = useState<ServiceLogTarget | null>(null);
  const { data: statuses } = useServiceStatuses(agent.id, { enabled: daemons.length > 0 });
  if (daemons.length === 0) return null;

  return (
    <section data-testid="agent-detail-servers">
      <IconSectionHeader
        icon={Server}
        className="mb-2 gap-1.5"
        labelAs="h3"
        labelClassName="font-semibold"
      >
        Servers
      </IconSectionHeader>
      <InfoPanel>
        <AgentServerList
          daemons={daemons}
          showStarted
          statuses={statuses?.services}
          onViewLogs={(daemon, service) =>
            setLogTarget({ serviceId: service.id, name: daemon.name })
          }
        />
      </InfoPanel>
      <ServiceLogSheet
        agentId={agent.id}
        target={logTarget}
        onOpenChange={(open) => {
          if (!open) setLogTarget(null);
        }}
      />
    </section>
  );
}
